import React, { useEffect, useState, useRef } from 'react';
import { Switch, Route, Redirect, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled, { createGlobalStyle } from 'styled-components';
import get from 'lodash.get';
import { message, Input, Modal as AntModal } from 'antd';
import ReactGA from 'react-ga4';
import { LastLocationProvider } from 'react-router-last-location';
import { initializeApp } from 'firebase/app';
import FooterMenu from './components/atoms/FooterMenu';
import notificationManager from './services/NotificationManager';
import fcmTokenHandler from './services/FCMTokenHandler';
import foregroundNotificationHandler from './services/ForegroundNotificationHandler';
import badgeManager from './services/BadgeManager';
import {
  getNotice, saveMyUserInfo,
} from './store/reducers/auth';
import { PrivateRoute } from './components/hoc/AuthRoutes';
import storage from './lib/storage';
import ScrollToTop from './components/containers/ScrollToTop';
import Container from './components/atoms/Container';
import {
  setAcceptLanguage, setContentLanguage, deleteLoginUserInfo, getAccessToken, setAnonymousHeader, setAuthHeader,
} from './utils/authorization';
import { addOrgFromCurrentUrl } from './utils/navigation';
import './App.css';
import 'antd/dist/antd.css';
import imgWelcome from './static/image/welcome_intelzbot.svg';
import { flexCenter } from './utils/mixins';
import { listenAndUploadContactsFromWebView, requestContactsFromNative } from './utils/contactUploader';
import {
  Login,
  ResetPassword,
  Page404,
  SearchResultCoverSelection,
  SearchResultCollection,
  SearchResultBooksAndPages,
  CollectionView,
  CollectionEditCollection,
  NeuronPage,
  ProducerList,
  CollectionPreview,
  UnpublishedCollectionDetail,
  Introduction,
  Mentoring,
  CreateCell,
  ScheduledMentoringList,
  ScheduledMentoringDetail,
  SuggestedMentoringList,
  MyCellHistory,
  SuggestedMentoringDetail,
  SuggestedMentoringAccept,
  FAQ,
  MY, // MyFollowList,
  ProducerMembershipVerify,
  ProducerMembership,
  Notice,
  HomeCollectionList,
  Privacy,
  ProducerCollectionEdit,
  HomeCollectionCategory,
  HomeCollectionSearchResult,
  Terms,
  AccountDeletion,
  CollectionReOrderCell,
  CollectionCreateCell,
  CellSelectCollection,
  DailyIssue,
  UserJisikTripList,
  PublicMemoListPage,
  PublicMemoCreatePage,
  MemoCreatePage,
  MemoViewPage,
  PaymentPage,
  PaymentResultPage,
  PaymentDetailPage,
  BannerDetailPage,
  BannerListPage,
  YouBookerPage,
  YouBookerCategoryPage,
  MyPublishRightPage,
  TogetherNoticeListPage,
  MessageListPage,
  CreateTogetherEventPage,
  TogetherEventViewPage,
  ProducerDetailEditPage,
  PurchaseSettlementPage,
  MyPurchaseEventHistoryPage,
  MyAccountInfoPage,
  MySettlementPage,
  OwnerAcceptPage,
  ImportBookPage,
  MarketingSelectPage,
  MarketingSNSPage,
  MarketingSMSPage,
  MarketingEmailPage,
  RedirectByHandlePage,
  CheeringListPage,
  MarketingEmailManagePage,
  MarketingEmailEditPage,
  MarketingSMSManagePage,
  MarketingSMSEditPage,
  ProfileViewPage,
  DetailedProfilePage,
  UserSettingPage,
  ContactManagePage,
  ContactEditPage,
  ContactCreatePage,
  HomePage,
  MyFollowListPage,
  MeetPage,
  InspectionPage,
  OrganizationManagePage,
  LectureVideoUploadPage,
  LectureVideoDetailPage,
  RecordedLectureListPage,
} from './components/pages';
import { onChangePathName } from './utils/googleAnalytics';
import { isReactNativeWebView } from './utils';

import * as privateAPI from './network/api/privateApi';
import translations from './utils/translations';

// const TRACKING_ID = 'UA-202220494-1'; // OUR_TRACKING_ID
// const TRACKING_ID = 'G-MRPPM8XKM1'; // OUR_TRACKING_ID - intelz.kr
const TRACKING_ID = 'G-TP6G8FC38X'; // OUR_TRACKING_ID - youbook.biz
ReactGA.initialize(TRACKING_ID);

const GlobalStyle = createGlobalStyle`
  body {
    font-family: ${({ lang }) => (lang === 'vi' ? 'Roboto, sans-serif' : lang === 'th' ? 'NotoSansTH400, sans-serif' : lang === 'ja' ? 'NotoSansJP400, sans-serif' : lang === 'zh' ? 'NotoSansSC400, sans-serif' : lang === 'ar' ? 'Noto Sans Arabic, sans-serif' : 'Noto Sans CJK KR, sans-serif')};

    direction: ${(props) => (props.lang === 'ar' ? 'rtl' : 'ltr')};
    text-align: ${(props) => (props.lang === 'ar' ? 'right' : 'left')};
  }
`;

const Layout = styled.div`
  color: black;
  background: ${(props) => (props.isYouBooker ? '#F3F5FA' : 'white')};
  width: 100%;
  padding: 28px 0 20px 0;
  @media screen and (min-width: 640px) {
    margin-top: 80px;
    padding: 0;
  }
`;

const Modal = styled(AntModal)`
  .ant-modal-content {
    border-radius: 10px;
  }

  .ant-modal-header {
    display: none;
  }

  .ant-modal-body {
    padding: 40px 24px;
  }

  .ant-modal-close-x {
    display: none;
  }
`;

const StyledModalContent = styled.div`
  ${flexCenter};
  flex-direction: column;
  color: black;

  .welcome-title {
    text-align: center;
    font-size: 17px;
    font-weight: bold;
    padding: 26px 0 20px;
  }

  .welcome-message {
    font-size: 15px;
    font-weight: 500;
  }

  .confirm {
    width: 120px;
    height: 40px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    background: black;
    color: white;
    ${flexCenter}
    border-radius: 30px;
    margin-top: 15px;
  }
`;

function App() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isModalVisible, setIsModalVisible] = useState({ welcome: false, needMoreInfo: false });
  const [moreInfo, setMoreInfo] = useState({ dummydata: '', mobile: '' }); // spread 연산자 사용 위해 dummydata property 사용, 후에 2개 이상이면 삭제
  const location = useLocation();

  // 알림 초기화 상태
  const [notificationInitialized, setNotificationInitialized] = useState(false);

  // org 파라미터 초기화 및 localStorage 관리
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const urlOrg = queryParams.get('org');
    const storedOrg = storage.get('org');

    // URL에 org가 있으면 localStorage에 저장
    if (urlOrg) {
      // localStorage에 저장된 값과 다르면 업데이트
      if (storedOrg !== urlOrg) {
        storage.set('org', urlOrg);
      }
    } else if (storedOrg && storedOrg !== 'acon') {
      // URL에 org가 없고 localStorage에 값이 있으면 URL에 추가 (acon이 아닌 경우)
      // 이미 URL에 org가 있는지 다시 확인 (무한 루프 방지)
      const currentQueryParams = new URLSearchParams(location.search);
      if (!currentQueryParams.get('org')) {
        const newQueryParams = new URLSearchParams(location.search);
        newQueryParams.set('org', storedOrg);
        history.replace({
          pathname: location.pathname, search: newQueryParams.toString(),
        });
      }
    }
    // 둘 다 없으면 기본값 'acon' 사용 (localStorage에 저장하지 않음)
  }, [location.pathname, location.search, history]);

  // history 객체를 래핑하여 org 파라미터 자동 유지 (acon 제외)
  useEffect(() => {
    const originalPush = history.push;
    const originalReplace = history.replace;

    history.push = function push(path, state) {
      const newPath = addOrgFromCurrentUrl(path, location);
      return originalPush.call(this, newPath, state);
    };

    history.replace = function replace(path, state) {
      const newPath = addOrgFromCurrentUrl(path, location);
      return originalReplace.call(this, newPath, state);
    };

    // cleanup
    return () => {
      history.push = originalPush;
      history.replace = originalReplace;
    };
  }, [history, location]);

  const userInfo = useSelector((state) => get(state, ['auth', 'userInfo']));
  const userInfoLoaded = useSelector((state) => get(state, ['auth', 'userInfoLoaded']));
  const userLanguage = useSelector((state) => (get(state, ['auth', 'userLanguage'])));
  const userNoticeUnReadCount = useSelector((state) => get(state, ['auth', 'userNoticeUnReadCount']));
  const lastNoticeCallTime = useRef(0);

  // Firebase 설정
  const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
  };

  // Firebase 앱 초기화 (한 번만)
  const firebaseApp = useRef(null);
  if (!firebaseApp.current) {
    try {
      firebaseApp.current = initializeApp(firebaseConfig);
    } catch (error) {
      console.error('[App] Firebase 초기화 실패:', error);
    }
  }

  useEffect(() => {
    // 다국어 지원 비활성화 - 한국어 고정
    // if (!storage.get('userLanguage')) {
    //   storage.set('userLanguage', navigator.language.split('-')[0]);
    // }
    // if (!storage.get('contentLanguage')) {
    //   storage.set('contentLanguage', navigator.language.split('-')[0]);
    // }
    storage.set('userLanguage', 'ko');
    storage.set('contentLanguage', 'ko');
    setAcceptLanguage('ko');
    setContentLanguage('ko');
    if (isReactNativeWebView()) {
      window.ReactNativeWebView.postMessage('LANGUAGE_ko');
    }
  }, []);

  // 권한 허용 후 초기화
  const initializeWithPermission = async () => {
    try {
      if (!firebaseApp.current) {
        console.error('[App] Firebase 앱이 초기화되지 않았습니다.');
        return;
      }

      // Service Worker 등록
      const registration = await notificationManager.registerServiceWorker();
      if (!registration) {
        console.error('[App] Service Worker 등록 실패');
        return;
      }

      // FCM 초기화 및 토큰 생성
      const token = await notificationManager.initializeFCM(firebaseApp.current);
      if (token) {
        // 토큰을 백엔드에 저장
        await fcmTokenHandler.saveTokenToBackend(token, userInfo.id);

        // 토큰 갱신 리스너 설정
        notificationManager.setupTokenRefreshListener(async (newToken) => {
          await fcmTokenHandler.refreshToken(newToken, userInfo.id);
        });

        // 포그라운드 메시지 리스너 설정
        notificationManager.setupForegroundMessageListener((payload) => {
          // Toast 표시
          foregroundNotificationHandler.handleForegroundMessage(payload);
        }, {
          showToast: true, updateBadge: true,
        });

        setNotificationInitialized(true);
      }
    } catch (error) {
      console.error('[App] 알림 초기화 실패:', error);
    }
  };

  // 알림 시스템 초기화
  useEffect(() => {
    let timeoutId = null;

    // 로그인한 사용자만 알림 초기화
    if (userInfo && userInfo.id && !notificationInitialized) {
      const initializeNotifications = async () => {
        try {
          // 브라우저 지원 확인
          const support = notificationManager.checkBrowserSupport();
          if (!support.serviceWorker || !support.notification) {
            console.warn('[App] 브라우저가 푸시 알림을 지원하지 않습니다.');
            return;
          }

          // 현재 권한 상태 확인
          const currentPermission = notificationManager.getPermissionStatus();

          if (currentPermission === 'default') {
            // 권한이 아직 요청되지 않았으면 바로 브라우저 권한 요청
            // 사용자 경험을 위해 약간의 딜레이 후 요청
            timeoutId = setTimeout(async () => {
              const permission = await notificationManager.requestPermission();
              if (permission === 'granted') {
                await initializeWithPermission();
              }
            }, 1000);
          } else if (currentPermission === 'granted') {
            // 이미 권한이 허용되어 있으면 바로 초기화
            await initializeWithPermission();
          }
        } catch (error) {
          console.error('[App] 알림 초기화 중 오류:', error);
        }
      };

      initializeNotifications().then();
    }

    // Cleanup: 타이머 정리
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [userInfo, notificationInitialized]);

  const onMoreInfoChange = (e) => {
    const { name, value } = e.target;
    setMoreInfo({ ...moreInfo, [name]: value });
  };

  const onMoreInfoClick = async () => {
    if (moreInfo.mobile.length !== 11) {
      message.warn('전화번호 11자리를 입력해주세요');
      return;
    }

    try {
      await privateAPI.updateUserMobile(moreInfo.mobile);
      message.info('입력되었습니다.');
      setIsModalVisible((state) => ({ ...state, needMoreInfo: false }));
    } catch (error) {
      console.error('전화번호 업데이트 실패:', error);
      message.error('입력에 실패했습니다.');
    }
  };

  useEffect(() => {
    onChangePathName(location.pathname);
    // 페이지 이동 시마다 알림 새로고침 (5초 이내 중복 호출 방지)
    if (userInfo) {
      const now = Date.now();
      const timeSinceLastCall = now - lastNoticeCallTime.current;
      const THROTTLE_TIME = 5000; // 5초

      if (timeSinceLastCall >= THROTTLE_TIME) {
        lastNoticeCallTime.current = now;
        dispatch(getNotice());
      }
    }
  }, [location.pathname, userInfo, dispatch]);

  // 읽지 않은 알림 개수가 변경되면 PWA 뱃지 업데이트
  useEffect(() => {
    if (typeof userNoticeUnReadCount === 'number') {
      badgeManager.setCount(userNoticeUnReadCount);
    }
  }, [userNoticeUnReadCount]);

  // useEffect(() => {
  //   // window.ChannelIO('hideMessenger');
  //   if (location.pathname.includes('/collection')
  //   || location.pathname === '/my/follows'
  //   || location.pathname.includes('suggested')
  //   || location.pathname === '/'
  //   || location.pathname === '/home/collection'
  //   || location.pathname.includes('/jisikTrip')) {
  //     if (!location.pathname.includes('/search/result/collection')) {
  //       return () => { };
  //     }
  //   }
  //   PullToRefresh.init({
  //     mainElement: '#main-layout',
  //     onRefresh() {
  //       window.location.reload();
  //     },
  //   });
  //   return () => {
  //     PullToRefresh.destroyAll();
  //   };
  // }, [location]);

  // 앱 처음 실행 시, 저장된 accessToken 을 가져오고, accessToken 이 있으면 로그인한 사용자 정보를 다시 한번 조회한다.
  useEffect(() => {
    const accessToken = getAccessToken();
    if (accessToken) {
      setAuthHeader(accessToken);
      privateAPI.getMyUserInfo()
        .then((res) => {
          dispatch(saveMyUserInfo(res.data.userInfo));
        })
        .catch((err) => {
          console.log('[ERROR] getMyUserInfo: ', err.message);
          deleteLoginUserInfo();
        });
    } else {
      setAnonymousHeader();
      deleteLoginUserInfo();
    }
  }, []);

  // 휴대폰 연락처 DB에 전송
  useEffect(() => {
    if (!userInfoLoaded || !userInfo) return;

    if (userInfo.id && !userInfo.mobile) {
      setIsModalVisible((prev) => ({ ...prev, needMoreInfo: true }));
    }
  }, [userInfoLoaded, userInfo]);

  // 리스너는 한 번만 등록
  useEffect(() => {
    const cleanup = listenAndUploadContactsFromWebView();
    return cleanup;
  }, []);

  // 로그인된 사용자의 연락처만 요청
  useEffect(() => {
    if (userInfoLoaded && userInfo?.id) {
      requestContactsFromNative();
    }
  }, [userInfoLoaded, userInfo?.id]);

  useEffect(() => {
    if (userInfo) {
      const { createdAt } = userInfo;
      const showWelcomeMessage = storage.get('showWelcomeMessage');
      const nowMs = Date.now();
      const createdMs = new Date(createdAt).getTime();
      const diffMinutes = (Number.isNaN(createdMs) ? Number.POSITIVE_INFINITY : (nowMs - createdMs) / 60000);

      // 개인회원 여부 묻지 않음
      if (!showWelcomeMessage && diffMinutes < 6) {
        setTimeout(() => {
          setIsModalVisible((state) => ({ ...state, welcome: true }));
          storage.set('showWelcomeMessage', 'true');
        }, [2000]);
      }
    }

    // 유저 알람 가져오기 + 앱에 알람 개수 보내기 (뱃지)
    if (userInfo) {
      dispatch(getNotice());

      // 유저 아이디 보내기 (기기 토큰과 함께 저장하여 푸시알림 보내기 위해)
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(`USER_ID_${userInfo.id}`);
      }
    }

    // React Native에서 보낸 메시지 수신
    const handleMessage = (event) => {
      // React Native WebView에서 보낸 메시지는 event.data로 받을 수 있음
      const eventMessage = event.data || event;

      if (eventMessage === 'REFRESH_NOTICE') {
        console.log('앱이 포그라운드로 전환됨, 알림 새로고침');
        if (userInfo) {
          dispatch(getNotice());
        }
      }
    };

    // window.addEventListener로 메시지 수신
    window.addEventListener('message', handleMessage);

    // React Native WebView의 경우 document.addEventListener도 함께 사용 (더 확실함)
    if (document) {
      document.addEventListener('message', handleMessage);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      if (document) {
        document.removeEventListener('message', handleMessage);
      }
    };
  }, [userInfo, dispatch]);

  // const fetchSequentialData = async () => {
  //   try {
  //     dispatch(getHotCollections('hot')); // 좋아요가 많은 미니북
  //   } catch (error) {
  //     console.error('API 요청 중 오류 발생:', error);
  //   }
  // };

  // useEffect(() => {
  //   fetchSequentialData();
  // }, []);

  function showMessage() {
    alert('이곳은 컬렉션 페이지입니다. 검색 페이지에서 선택한 셀들을 보여 드립니다.');
    history.push('/search');
  }

  return (
    <Layout isYouBooker={location.pathname === '/member'}>
      <GlobalStyle lang={userLanguage} />
      <ScrollToTop updateWithPath />

      <Route
        path="/collection/:id"
        render={(props) => {
          // eslint-disable-next-line react/prop-types
          const { id } = props.match.params;
          if (id) storage.set('lastVisitedCollection', id);
        }}
      />
      <Modal
        footer={null}
        centered
        visible={isModalVisible.welcome}
        onCancel={() => {
          setIsModalVisible((state) => ({ ...state, welcome: false }));
        }}
        width={320}
        height={400}
      >
        <StyledModalContent>
          <div className="welcome-image">
            <img src={imgWelcome} alt="" />
          </div>
          <div className="welcome-title">{translations[userLanguage].register_welcome_message}</div>
          <div
            className="confirm"
            onClick={() => {
              setIsModalVisible((state) => ({ ...state, welcome: false }));
            }}
          >
            {translations[userLanguage].button_confirm}
          </div>
        </StyledModalContent>
      </Modal>
      <Modal
        footer={null}
        centered
        visible={isModalVisible.needMoreInfo && !userInfo?.autoImported}
        onCancel={() => {
          setIsModalVisible((state) => ({ ...state, needMoreInfo: false }));
          message.error('입력에 실패했습니다.');
        }}
        width={320}
        height={400}
      >
        <StyledModalContent>
          <div className="welcome-image">
            <img src={imgWelcome} alt="" />
          </div>
          <div className="welcome-message" style={{ textAlign: 'center', margin: '30px 0' }}>
            원활한 서비스 이용을 위해<br />
            추가 정보가 필요합니다<br />
          </div>
          <Input name="mobile" value={moreInfo.mobile} onChange={onMoreInfoChange} placeholder="핸드폰 번호 ( - 제외하고 입력)" />
          <div className="confirm" onClick={onMoreInfoClick}>
            확인
          </div>
        </StyledModalContent>
      </Modal>
      <div id="main-layout">
        <Container id="container" maxWidth={location.pathname === '/' ? '872px' : undefined}>
          <LastLocationProvider>
            <Switch>
              <Route path="/user/follow-list" component={MyFollowListPage} />
              <Route path="/user-setting" component={UserSettingPage} />
              <Route path="/" component={HomePage} exact />
              <Route path="/login" component={Login} exact />
              <Route path="/login/:provider/callback" component={Login} exact />
              <Route path="/reset-password" component={ResetPassword} exact />
              <Route path="/search/result/books-and-pages" component={SearchResultBooksAndPages} />
              <Route path="/search/result/collection/:questionId" component={HomeCollectionSearchResult} />
              <Route path="/search/result/collection" component={HomeCollectionSearchResult} />
              <Route path="/search/result" component={NeuronPage} />
              <Route path="/home/collection/:category" component={HomeCollectionCategory} />
              <Route path="/home/collection" component={HomeCollectionList} />
              <Route path="/neuron/collection/cover" component={SearchResultCoverSelection} />
              <Route path="/neuron/collection/:cellId" component={SearchResultCollection} />
              <Route path="/neuron/collection" component={SearchResultCollection} />
              <Route
                path="/collection"
                exact
                render={() => {
                  const lastVisitedCollection = storage.get('lastVisitedCollection');
                  if (lastVisitedCollection === 'neuron') return <Redirect to="/neuron/collection" />;
                  if (lastVisitedCollection) return <Redirect to={`/collection/${lastVisitedCollection}`} />;
                  return showMessage();
                }}
              />
              <Route path="/collection/preview/:id" component={CollectionPreview} />
              <Route path="/collection/unpublished/:collectionId" component={UnpublishedCollectionDetail} />
              <Route path="/collection/reorder/:id" component={CollectionReOrderCell} /> {/* 컬렉션에서 셀 순서 조정 */}
              <Route
                path="/collection/create/cell/:collectionId"
                component={CollectionCreateCell}
              /> {/* 컬렉션에서 지식셀 생성 */}
              <PrivateRoute path="/collection/:id/edit" component={CollectionEditCollection} />
              <Route path="/collection/:id/:prevSlide" component={CollectionView} />
              <Route path="/collection/:id/" component={CollectionView} />
              <Route path="/producer/membership/verify" component={ProducerMembershipVerify} />
              <Route path="/producer/membership" component={ProducerMembership} />
              <Route path="/producer/:pdId/edit" component={ProducerDetailEditPage} />
              <Route path="/producer/:pdId/:id/collectionEdit" component={ProducerCollectionEdit} />
              <Route path="/producer/:pdId/:id" render={({ match }) => <Redirect to={`/p/${match.params.pdId}`} />} />
              <Route path="/producer/:id" component={ProfileViewPage} />
              <Route path="/producer" component={ProducerList} />
              <Route path="/profile/:id" component={ProfileViewPage} />
              <Route path="/p/:id" component={ProfileViewPage} />
              <Route path="/p/:handle" component={ProfileViewPage} />
              <Route path="/detailed-profile/:id" component={DetailedProfilePage} />
              <Route path="/intro" component={Introduction} />
              <Route path="/mentoring" component={Mentoring} />
              <Route path="/meet/:id" component={MeetPage} />
              <Route path="/lecture/videos" component={RecordedLectureListPage} />
              <Route path="/lecture/video/upload" component={LectureVideoUploadPage} />
              <Route path="/lecture/video/:id/edit" component={LectureVideoUploadPage} />
              <Route path="/lecture/video/:id" component={LectureVideoDetailPage} />
              <Route path="/cell/create/:collectionId" component={CreateCell} />
              <Route path="/cell/select/collection/:id" component={CellSelectCollection} /> {/* 지식셀에서 컬렉션에 저장 */}
              <Route path="/cell/update/:id/:collectionId/:previousSelectedLanguage/:prevSlide" component={CreateCell} />
              <Route path="/cell/update/:id/:collectionId/:previousSelectedLanguage" component={CreateCell} />
              <Route path="/cell/update/:id" component={CreateCell} />
              <Route path="/cell/history" component={MyCellHistory} />
              <Route path="/together/event/:togetherId/:eventId/create" component={CreateTogetherEventPage} />
              <Route path="/together/event/:togetherId/:eventId/edit" component={CreateTogetherEventPage} />
              <Route path="/together/event/:togetherId/:eventId" component={TogetherEventViewPage} />
              <Route path="/message/:type/:id" component={MessageListPage} />
              <Route path="/scheduled/:id/:pdId/list" component={TogetherNoticeListPage} />
              <Route path="/scheduled/:id/edit" component={SuggestedMentoringAccept} />
              <Route path="/scheduled/:id" component={ScheduledMentoringDetail} />
              <Route path="/scheduled" component={ScheduledMentoringList} />
              <Route path="/suggested/:id/accept" component={SuggestedMentoringAccept} />
              <Route path="/suggested/:id" component={SuggestedMentoringDetail} />
              <Route path="/suggested" component={SuggestedMentoringList} />
              <Route path="/faq" component={FAQ} />
              <Route path="/my/importBooks" component={ImportBookPage} />
              <Route path="/my/accountInfo" component={MyAccountInfoPage} />
              <Route path="/my/purchaseEventHistory" component={MyPurchaseEventHistoryPage} />
              <Route path="/my/settlement" component={MySettlementPage} />
              <Route path="/my/publishRight" component={MyPublishRightPage} />
              <Route path="/marketing/select" component={MarketingSelectPage} />
              <Route path="/marketing/manage" component={MarketingEmailManagePage} />
              <Route path="/marketing/sms/:type/edit/:id" component={MarketingSMSEditPage} />
              <Route path="/marketing/sms/:type/manage" component={MarketingSMSManagePage} />
              <Route path="/marketing/sms/:type" component={MarketingSMSPage} />
              <Route path="/marketing/email/:type/edit/:id" component={MarketingEmailEditPage} />
              <Route path="/marketing/email/contact/create" component={MarketingEmailEditPage} />
              <Route path="/marketing/email/:type/manage" component={MarketingEmailManagePage} />
              <Route path="/marketing/email/:type" component={MarketingEmailPage} />
              <Route path="/marketing/sns/:type" component={MarketingSNSPage} />
              <Route path="/contact/create" component={ContactCreatePage} />
              <Route path="/contact/:id/edit" component={ContactEditPage} />
              <Route path="/contact/manage" component={ContactManagePage} />
              <PrivateRoute path="/organization/:orgKey/manage" component={OrganizationManagePage} />
              {/* <Route path="/my/follows" component={MyFollowList} /> */}
              <Route path="/my" component={MY} />
              <Route path="/notice" component={Notice} />
              <Route path="/privacy" component={Privacy} />
              <Route path="/terms" component={Terms} />
              <Route path="/account-deletion" component={AccountDeletion} />
              <Route path="/dailyIssue/:id" component={DailyIssue} />
              <Route path="/user/jisikTrip/:id/:type" component={UserJisikTripList} />
              <Route path="/memo/public/create" component={PublicMemoCreatePage} />
              <Route path="/memo/public/:id" component={MemoViewPage} />
              <Route path="/memo/public" component={PublicMemoListPage} />
              <Route path="/memo/my/:id" component={MemoViewPage} />
              <Route path="/memo/create" component={MemoCreatePage} />
              <Route path="/memo/:id/edit" component={MemoCreatePage} />
              <Route path="/payment/result/:type/:id" component={PaymentResultPage} />
              <Route path="/payment/result/:type/" component={PaymentResultPage} />
              <Route path="/payment/detail" component={PaymentDetailPage} />
              <Route exact path="/payment" component={PaymentPage} />
              <Route path="/banner/detail/:id" component={BannerDetailPage} />
              <Route path="/banner/list" component={BannerListPage} />
              <Route path="/member/category" component={YouBookerCategoryPage} />
              <Route path="/member" component={YouBookerPage} exact />
              <Route path="/inspection" component={InspectionPage} />
              <Route path="/owners/:collectionId/:pdId" component={OwnerAcceptPage} />
              <Route path="/purchaseSettlement" component={PurchaseSettlementPage} />
              <Route path="/cheers/:type/:pdId" component={CheeringListPage} />
              <Route path="/:handle/p/:localId" component={RedirectByHandlePage} />
              <Route path="/:handle/m/:localId" component={RedirectByHandlePage} />
              <Route path="/:handle/e/:spaceId/:eventId" component={RedirectByHandlePage} />
              <Route path="/:handle/e/:localId" component={RedirectByHandlePage} />
              <Route path="/:handle" component={RedirectByHandlePage} />
              <Route component={Page404} />
            </Switch>
          </LastLocationProvider>
        </Container>
        {(!location.pathname.includes('/search/result'))
          && (location.pathname !== '/home')
          && (location.pathname !== '/home/cell')
          && (location.pathname !== '/home/collection')
          && <FooterMenu />}
      </div>
    </Layout>
  );
}

export default App;
